import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, parseApiResponse } from '../../api';
import TranscriptViewer from '../TranscriptViewer';
import SummaryView from './SummaryView';
import EmptyState from './EmptyState';
import StatusBadge from './StatusBadge';
import StudentLinkDropdown from './StudentLinkDropdown';

/**
 * TranscriptDetailPanel - Main detail view with tabbed navigation
 * Improved with better accessibility and empty states
 */
const TranscriptDetailPanel = ({
  transcript,
  students,
  onStudentLinked,
  onSummaryUpdated,
  onAiSummarize,
  onRetrySection,
  retryingSectionKey,
  isAnalyzingTranscript,
  formatDate,
  formatDuration,
  readOnly = false,
}) => {
  const [activeTab, setActiveTab] = useState('transcript');
  const [summary, setSummary] = useState(null);
  const [summarySource, setSummarySource] = useState(null);
  const [summaryGeneratedAt, setSummaryGeneratedAt] = useState(null);
  const [googleNotesDocId, setGoogleNotesDocId] = useState(transcript?.google_notes_doc_id || null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [linkedStudentUuid, setLinkedStudentUuid] = useState(transcript?.linked_student_uuid);
  const [linkingStudent, setLinkingStudent] = useState(false);
  const [googleNotes, setGoogleNotes] = useState(null);
  const [googleNotesDocUrl, setGoogleNotesDocUrl] = useState(null);
  const [googleNotesDocTitle, setGoogleNotesDocTitle] = useState(null);
  const [googleNotesLoading, setGoogleNotesLoading] = useState(false);
  const [googleNotesError, setGoogleNotesError] = useState(null);

  const fetchSummary = useCallback(async (forceRegenerate = false) => {
    if (!transcript?.id) return;

    setLoadingSummary(true);
    setSummaryError(null);

    try {
      const response = await apiFetch(`/api/google/meet/transcripts/${transcript.id}/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_regenerate: forceRegenerate })
      });
      const result = await parseApiResponse(response, 'google-meet-summary-detail-panel');
      const data = result.data;
      if (!result.ok) {
        setSummaryError(result.error.message || 'Failed to generate summary');
        return;
      }

      if (data.error && !data.summary) {
        setSummaryError(data.error);
      } else {
        setSummary(data.summary);
        setSummarySource(data.source);
        setSummaryGeneratedAt(data.generated_at);
        setGoogleNotesDocId(data.google_notes_doc_id || null);
        if (onSummaryUpdated) {
          onSummaryUpdated(transcript.id, data.summary, data.source, data.generated_at);
        }
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
      setSummaryError('Failed to generate summary');
    } finally {
      setLoadingSummary(false);
    }
  }, [transcript?.id, onSummaryUpdated]);

  const handleStudentLink = async (studentUuid) => {
    if (!transcript?.id) return;

    setLinkingStudent(true);

    try {
      const response = await apiFetch(`/api/google/meet/transcripts/${transcript.id}/student`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_uuid: studentUuid })
      });
      const result = await parseApiResponse(response, 'google-meet-student-link');
      const data = result.data;
      if (!result.ok) {
        return;
      }

      if (data.success) {
        setLinkedStudentUuid(studentUuid);
        if (onStudentLinked) {
          onStudentLinked(transcript.id, studentUuid);
        }
      }
    } catch (err) {
      console.error('Error linking student:', err);
    } finally {
      setLinkingStudent(false);
    }
  };

  // Reset state and fetch summary when transcript changes
  useEffect(() => {
    setSummary(transcript?.summary || null);
    setSummarySource(transcript?.summary_source || null);
    setGoogleNotesDocId(transcript?.google_notes_doc_id || null);
    setLinkedStudentUuid(transcript?.linked_student_uuid || null);
    setSummaryError(null);
    setGoogleNotes(null);
    setGoogleNotesDocUrl(null);
    setGoogleNotesDocTitle(null);
    setGoogleNotesError(null);
  }, [transcript?.id, transcript?.summary, transcript?.summary_source, transcript?.google_notes_doc_id, transcript?.linked_student_uuid]);

  // Fetch summary when on summary tab with no cached summary
  useEffect(() => {
    if (activeTab === 'summary' && transcript?.id && !summary && !loadingSummary) {
      fetchSummary(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, transcript?.id, summary, loadingSummary, fetchSummary]);

  // Fetch raw Google Notes when the notes tab is opened
  const fetchGoogleNotes = useCallback(async () => {
    if (!transcript?.id) return;
    setGoogleNotesLoading(true);
    setGoogleNotesError(null);
    try {
      const response = await apiFetch(`/api/google/meet/transcripts/${transcript.id}/notes`);
      const result = await parseApiResponse(response, 'google-meet-notes');
      if (!result.ok) {
        setGoogleNotesError(result.error?.message || 'Failed to load notes');
        return;
      }
      setGoogleNotes(result.data.notes || null);
      setGoogleNotesDocUrl(result.data.doc_url || null);
      setGoogleNotesDocTitle(result.data.doc_title || null);
      if (!result.data.notes) {
        setGoogleNotesError(result.data.message || 'No Gemini notes available for this session.');
      }
    } catch (err) {
      setGoogleNotesError('Failed to load Google notes');
    } finally {
      setGoogleNotesLoading(false);
    }
  }, [transcript?.id]);

  useEffect(() => {
    if (activeTab === 'notes' && transcript?.id && googleNotes === null && !googleNotesLoading && !googleNotesError) {
      fetchGoogleNotes();
    }
  }, [activeTab, transcript?.id, googleNotes, googleNotesLoading, googleNotesError, fetchGoogleNotes]);

  // Determine if transcript content is available
  const hasTranscriptContent = transcript?.transcript_content && transcript.transcript_content.length > 0;
  const analysisStatus = (typeof summary === 'object' && summary?.analysis_status) || transcript?.analysis_status;

  if (!transcript) {
    return (
      <div className="transcript-detail-panel empty">
        <EmptyState variant="no-selection" />
      </div>
    );
  }

  // Check if transcript is still processing
  const isProcessing = !hasTranscriptContent && transcript.sync_status !== 'FAILED';

  return (
    <div className="transcript-detail-panel">
      {/* Header */}
      <div className="detail-header">
        <div className="detail-header-main">
          <h2>{transcript.meeting_title || 'Meeting Transcript'}</h2>
          <div className="detail-header-badges">
            {hasTranscriptContent ? (
              <StatusBadge variant="success" size="small">Available</StatusBadge>
            ) : isProcessing ? (
              <StatusBadge variant="warning" size="small">Processing</StatusBadge>
            ) : (
              <StatusBadge variant="error" size="small">Unavailable</StatusBadge>
            )}
            {summarySource === 'GOOGLE' && (
              <StatusBadge variant="info" size="small">Google Notes</StatusBadge>
            )}
            {summarySource === 'AI_GENERATED' && analysisStatus === 'COMPLETE' && (
              <StatusBadge variant="success" size="small">AI Complete</StatusBadge>
            )}
            {summarySource === 'AI_GENERATED' && analysisStatus === 'PARTIAL' && (
              <StatusBadge variant="warning" size="small">AI Partial</StatusBadge>
            )}
            {summarySource === 'AI_GENERATED' && analysisStatus === 'FAILED' && (
              <StatusBadge variant="error" size="small">AI Failed</StatusBadge>
            )}
            {isAnalyzingTranscript && (
              <StatusBadge variant="warning" size="small">AI Summarizing</StatusBadge>
            )}
          </div>
        </div>
        {!readOnly && onAiSummarize && (
          <div className="detail-header-actions">
            <button
              className="btn btn-secondary btn-small"
              onClick={() => onAiSummarize(transcript.id)}
              disabled={isAnalyzingTranscript}
            >
              {isAnalyzingTranscript ? 'Summarizing...' : 'AI Summarize'}
            </button>
          </div>
        )}
        <div className="detail-meta">
          <span className="meta-item">
            <span className="meta-label">Date:</span>
            {formatDate(transcript.start_time)}
          </span>
          <span className="meta-item">
            <span className="meta-label">Duration:</span>
            {formatDuration(transcript.start_time, transcript.end_time)}
          </span>
          {transcript.meeting_code && (
            <span className="meta-item">
              <span className="meta-label">Code:</span>
              <code className="meeting-code">{transcript.meeting_code}</code>
            </span>
          )}
        </div>
        {summaryGeneratedAt && (
          <div className="detail-timestamp">
            Last updated: {new Date(summaryGeneratedAt).toLocaleString()}
          </div>
        )}
      </div>

      {/* Tab Navigation with ARIA */}
      <div className="tab-navigation" role="tablist" aria-label="Transcript sections">
        <button
          className={`tab-btn ${activeTab === 'transcript' ? 'active' : ''}`}
          onClick={() => setActiveTab('transcript')}
          role="tab"
          aria-selected={activeTab === 'transcript'}
          aria-controls="panel-transcript"
          id="tab-transcript"
        >
          Transcript
        </button>
        <button
          className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
          role="tab"
          aria-selected={activeTab === 'notes'}
          aria-controls="panel-notes"
          id="tab-notes"
        >
          Gemini Notes
        </button>
        <button
          className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
          role="tab"
          aria-selected={activeTab === 'summary'}
          aria-controls="panel-summary"
          id="tab-summary"
        >
          AI Summary
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'notes' && (
          <div
            className="notes-content-wrapper"
            role="tabpanel"
            id="panel-notes"
            aria-labelledby="tab-notes"
          >
            {googleNotesLoading ? (
              <div className="summary-view loading-state">
                <div className="summary-loading">
                  <div className="loading-spinner"></div>
                  <p>Fetching Gemini notes...</p>
                  <p className="loading-hint">Reading from Google Docs</p>
                </div>
              </div>
            ) : googleNotesError && !googleNotes ? (
              <div className="summary-view error-state">
                <div className="summary-error">
                  <div className="error-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                      <line x1="12" y1="9" x2="12" y2="13"></line>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                  </div>
                  <h4>Unable to Load Gemini Notes</h4>
                  <p className="error-detail">{googleNotesError}</p>
                  <div className="error-suggestions">
                    <p>Suggestions:</p>
                    <ul>
                      <li>Enable "Take notes for me" in Google Meet before the session</li>
                      <li>Wait 10–30 minutes after the meeting ends for notes to generate</li>
                      <li>Check that the meeting host has access to the notes document</li>
                    </ul>
                  </div>
                  <button className="btn btn-secondary" onClick={fetchGoogleNotes}>
                    Try Again
                  </button>
                </div>
              </div>
            ) : googleNotes ? (
              <div className="google-notes-view">
                <div className="google-notes-header">
                  <div className="google-notes-meta">
                    <span className="badge badge-google">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                      Gemini Auto-Notes
                    </span>
                    {googleNotesDocTitle && (
                      <span className="notes-doc-title">{googleNotesDocTitle}</span>
                    )}
                  </div>
                  <div className="google-notes-actions">
                    {googleNotesDocUrl && (
                      <a
                        href={googleNotesDocUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary btn-small"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                        Open in Docs
                      </a>
                    )}
                    <button className="btn btn-secondary btn-small" onClick={fetchGoogleNotes}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                        <path d="M21 3v5h-5"></path>
                      </svg>
                      Refresh
                    </button>
                  </div>
                </div>
                <pre className="google-notes-content">{googleNotes}</pre>
              </div>
            ) : (
              <div className="summary-view empty-state">
                <div className="no-summary-content">
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="12" y1="18" x2="12" y2="12"></line>
                      <line x1="9" y1="15" x2="15" y2="15"></line>
                    </svg>
                  </div>
                  <h4>No Gemini Notes</h4>
                  <p>This meeting has no auto-generated notes linked. Enable "Take notes for me" in Google Meet to have Gemini automatically generate notes during future sessions.</p>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'transcript' && (
          <div
            className="transcript-content-wrapper"
            role="tabpanel"
            id="panel-transcript"
            aria-labelledby="tab-transcript"
          >
            {students && students.length > 0 && (
              <div className="summary-section student-link-section transcript-student-link-section">
                <StudentLinkDropdown
                  students={students}
                  selectedUuid={linkedStudentUuid}
                  onSelect={handleStudentLink}
                  loading={linkingStudent}
                />
              </div>
            )}
            {hasTranscriptContent ? (
              <TranscriptViewer content={transcript.transcript_content} />
            ) : isProcessing ? (
              <EmptyState variant="processing" />
            ) : (
              <EmptyState variant="unavailable" />
            )}
          </div>
        )}
        {activeTab === 'summary' && (
          <div
            role="tabpanel"
            id="panel-summary"
            aria-labelledby="tab-summary"
          >
            <SummaryView
              summary={summary}
              summarySource={summarySource}
              summaryGeneratedAt={summaryGeneratedAt}
              googleNotesDocId={googleNotesDocId}
              loading={loadingSummary}
              error={summaryError}
              onRegenerate={readOnly ? undefined : () => fetchSummary(true)}
              onRetrySection={readOnly ? undefined : (sectionKey => onRetrySection && onRetrySection(transcript.id, sectionKey))}
              retryingSectionKey={retryingSectionKey}
              onAiSummarize={readOnly ? undefined : (() => onAiSummarize && onAiSummarize(transcript.id))}
              isAnalyzingTranscript={isAnalyzingTranscript}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptDetailPanel;
